import { OverlayContainer } from "@angular/cdk/overlay";
import { Injectable } from "@angular/core";

export type MeetingTheme = "dark" | "light";

const STORAGE_KEY = "du-meeting-theme";
const THEME_CLASSES = ["meeting-theme-dark", "meeting-theme-light"];

/**
 * Dark/light theme for the meeting screens. They follow the light design, so the theme starts
 * light and no screen offers a switch; a dark choice saved by the old toggle is not read back.
 * `set()` / `toggle()` and the dark tokens stay for when a switch returns.
 *
 * Components apply `hostClass` to their host element; the colour tokens in `_teams-theme.scss`
 * hang off that class. Material menus render in the overlay container outside the component
 * tree, so while a meeting screen is open the container carries the class too.
 */
@Injectable({ providedIn: "root" })
export class MeetingThemeService {
  theme: MeetingTheme = "light";
  private attached = 0;

  constructor(private overlay: OverlayContainer) {}

  get hostClass(): string {
    return "meeting-theme-" + this.theme;
  }

  get isDark(): boolean {
    return this.theme === "dark";
  }

  toggle() {
    this.set(this.isDark ? "light" : "dark");
  }

  set(theme: MeetingTheme) {
    this.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Storage can be blocked; the theme still applies for this visit.
    }
    if (this.attached) this.applyToOverlay();
  }

  /** Call from ngOnInit of a component that shows themed menus; pair with detach(). */
  attach() {
    this.attached++;
    this.applyToOverlay();
  }

  detach() {
    this.attached = Math.max(0, this.attached - 1);
    if (!this.attached) this.overlay.getContainerElement().classList.remove(...THEME_CLASSES);
  }

  private applyToOverlay() {
    const container = this.overlay.getContainerElement();
    container.classList.remove(...THEME_CLASSES);
    container.classList.add(this.hostClass);
  }
}
